import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  claimsImportHistory,
  claimsImportBatchClaims,
  claimsImportBatchDelete,
} from '../BaseURL';
import { IconRecords, IconCheck, IconClose, IconTrash, IconChevron } from './icons';
import notify from './notify';
import './ImportHistory.css';

// ============================================================================
//  ImportHistory.js — a record of every XML batch that has been imported
// ============================================================================
//
//  Written for this platform; the standalone project had no equivalent. It only
//  ever polled the job it had just started, so once the page was closed there
//  was no way to tell whether a facility's month had already been loaded — you
//  would re-import and read the duplicate count to find out.
//
//  One row per batch: which facility, which month the XML covers, how many
//  claims went in, and how many are still there. Collapsed by default, because
//  the point of the screen is importing — the history is what you open when you
//  need to check.
//
//  Each row does two more things:
//
//    EXPAND   opens the batch and shows what is inside it — a summary strip and
//             the claims themselves, paged. Fetched on first expand, not with
//             the list: a batch is thousands of claims and loading all of them
//             for every row would make opening the card slow for no reason.
//
//    DELETE   removes the batch and its claims. This is the only destructive
//             control on the screen, so it is treated like one — see confirm()
//             below. The backend independently refuses to touch anything that is
//             not source = 'xml_import' from this batch, so a mis-click cannot
//             reach a hand-entered claim.

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—'
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const fmtTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? ''
    : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
};

// Ghana cedis. Intl rather than a hand-rolled toFixed so thousands separators
// and the symbol follow the browser's locale.
const fmtMoney = (v) => {
  const n = Number(v || 0);
  try {
    return n.toLocaleString(undefined, {
      style: 'currency', currency: 'GHS', minimumFractionDigits: 2,
    });
  } catch {
    return `GHS ${n.toFixed(2)}`;
  }
};

const fmtNum = (v) => Number(v || 0).toLocaleString();

const PAGE = 25;

export default function ImportHistory({
  facilityId, refreshKey, onChanged,
  //  Rendered at the right of the header. The screen's New import button lives
  //  here rather than in a card of its own: the records ARE the screen now, and an
  //  action that adds to a list belongs on the list.
  action,
  //  The batch that just landed, marked for a moment so it is findable among the
  //  others rather than appearing silently at the top.
  highlightJobId,
}) {
  //  OPEN BY DEFAULT. This card was one of two on the screen and collapsing it
  //  made room for the other; it is now the whole screen, and a screen that opens
  //  collapsed shows the user nothing they came for.
  const [open, setOpen] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Which batch is expanded, and what we have fetched for it. Keyed by job id so
  // collapsing and re-expanding does not refetch, but a delete can drop the
  // entry and force a fresh read.
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({});     // id -> { loading, error, job, summary, claims, page }
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(claimsImportHistory, {
        params: facilityId ? { facilityId } : {},
      });
      setJobs(data?.jobs || []);
      setLoaded(true);
    } catch (err) {
      // A missing history endpoint should not break the import screen above it.
      console.error('[import history]', err?.response?.data?.error || err.message);
      setJobs([]);
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  // Fetch when opened, and again whenever a commit finishes — refreshKey is
  // bumped by the parent so a batch appears here without a manual reload.
  useEffect(() => {
    if (open) load();
  }, [open, load, refreshKey]);

  // Keep the count visible while collapsed, so the header is worth reading.
  useEffect(() => {
    if (!loaded) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // ── Expanding a batch ─────────────────────────────────────────────────────
  const fetchDetail = useCallback(async (id, offset = 0) => {
    setDetails((d) => ({
      ...d,
      [id]: { ...(d[id] || {}), loading: true, error: null },
    }));
    try {
      const { data } = await axios.get(claimsImportBatchClaims(id), {
        params: { limit: PAGE, offset },
      });
      setDetails((d) => ({
        ...d,
        [id]: {
          loading: false,
          error: null,
          job: data.job,
          summary: data.summary,
          claims: data.claims || [],
          page: data.page || { limit: PAGE, offset, returned: 0 },
        },
      }));
    } catch (err) {
      setDetails((d) => ({
        ...d,
        [id]: {
          ...(d[id] || {}),
          loading: false,
          error: err?.response?.data?.error || err.message,
        },
      }));
    }
  }, []);

  const toggleExpand = (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    // Only fetch the first time, or after a delete cleared it.
    if (!details[id]?.claims) fetchDetail(id, 0);
  };

  // ── Deleting a batch ──────────────────────────────────────────────────────
  //  Two-step on purpose. The first confirm names the facility, the month and
  //  the row count, because "are you sure?" with no numbers in it is a dialog
  //  people learn to click through. The second only appears when the backend
  //  reports the batch contains claims already sent to the NHIA, and it requires
  //  the word DELETE to be typed — at that point the operator is destroying a
  //  financial record, and a click is too cheap.
  const remove = async (job) => {
    const present = Number(job.claims_present || 0);
    const ok = await notify.confirm(
      'Delete this imported batch?',
      `${job.facility_name || 'This facility'} · ${job.month_year || 'no month'} — `
      + `${fmtNum(present)} claim${present === 1 ? '' : 's'} will be removed from the database, `
      + 'along with their diagnoses, services, procedures, investigations and medications. '
      + 'This cannot be undone; the batch would have to be imported again.',
      { icon: 'warning', confirmText: `Delete ${fmtNum(present)} claims`, cancelText: 'Keep' }
    );
    if (!ok) return;

    setDeletingId(job.id);
    try {
      const { data } = await axios.delete(claimsImportBatchDelete(job.id));
      afterDelete(job, data);
    } catch (err) {
      const body = err?.response?.data;

      // 409 — the batch holds submitted/approved/paid claims. Say exactly what,
      // and make the override deliberate.
      if (err?.response?.status === 409 && body?.requiresForce) {
        const detail = (body.locked || []).map((r) => `${r.n} ${r.status}`).join(', ');
        const typed = await notify.prompt(
          'These claims have already been sent',
          `This batch contains ${detail}. Deleting them removes a record the NHIA `
          + 'has already received. Type DELETE to confirm.',
          {
            icon: 'warning', required: true, placeholder: 'DELETE',
            confirmText: 'Force delete', cancelText: 'Cancel',
            requiredMessage: 'Type DELETE to confirm',
          }
        );
        if (String(typed || '').trim().toUpperCase() !== 'DELETE') {
          setDeletingId(null);
          if (typed !== null) notify.toast.info('Not deleted');
          return;
        }
        try {
          const { data } = await axios.delete(claimsImportBatchDelete(job.id, true));
          afterDelete(job, data, true);
        } catch (err2) {
          notify.error('Could not delete the batch', err2?.response?.data?.error || err2.message);
        } finally {
          setDeletingId(null);
        }
        return;
      }

      notify.error('Could not delete the batch', body?.error || err.message);
    } finally {
      setDeletingId((cur) => (cur === job.id ? null : cur));
    }
  };

  const afterDelete = (job, data, forced) => {
    notify.toast.success(
      `${fmtNum(data?.deleted ?? 0)} claim${Number(data?.deleted) === 1 ? '' : 's'} deleted`
      + (forced ? ' (forced)' : '')
    );
    // Drop the row locally so the table updates immediately, then reload to stay
    // honest about what the server actually holds.
    setJobs((list) => list.filter((j) => j.id !== job.id));
    setDetails((d) => { const next = { ...d }; delete next[job.id]; return next; });
    if (expandedId === job.id) setExpandedId(null);
    load();
    if (typeof onChanged === 'function') onChanged();
  };

  const done   = jobs.filter((j) => j.status === 'done').length;
  const failed = jobs.filter((j) => j.status === 'failed').length;

  return (
    <section className={`ih-card ${open ? 'is-open' : ''}`}>
      {/*  The toggle and the action are SIBLINGS in a row, not nested. The whole
           header used to be one <button>; putting the New import button inside it
           would be a button within a button, which is invalid and behaves
           differently in every browser. */}
      <div className="ih-head-row">
        <button
          type="button"
          className="ih-head"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          <span className="ih-chev" aria-hidden="true">▾</span>
          <span className="ih-head-icon"><IconRecords size={18} /></span>
          <span className="ih-head-copy">
            <span className="ih-title">Import records</span>
            <span className="ih-sub">
              {!loaded ? 'Loading…'
                : jobs.length === 0 ? 'Nothing imported yet'
                : `${jobs.length} batch${jobs.length === 1 ? '' : 'es'}`
                  + (done ? ` · ${done} completed` : '')
                  + (failed ? ` · ${failed} failed` : '')}
            </span>
          </span>
          <span className="ih-toggle">{open ? 'Hide' : 'Show'}</span>
        </button>
        {action && <div className="ih-head-action">{action}</div>}
      </div>

      {open && (
        <div className="ih-body">
          {loading && <div className="ih-status">Loading import records…</div>}

          {!loading && jobs.length === 0 && (
            <div className="ih-empty">
              <div className="ih-empty-title">No imports yet</div>
              <p>Once you commit a batch it appears here with its facility and month.</p>
            </div>
          )}

          {!loading && jobs.length > 0 && (
            <div className="ih-table-wrap">
              <table className="ih-table">
                <thead>
                  <tr>
                    <th className="ih-x-col"><span className="sr-only">Expand</span></th>
                    <th>Facility</th>
                    <th>Month covered</th>
                    <th>File</th>
                    <th className="num">Claims</th>
                    <th className="num">Still present</th>
                    <th>Imported</th>
                    <th>Status</th>
                    <th className="ih-act-col"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => {
                    const isOpen = expandedId === j.id;
                    const d = details[j.id];
                    //  The batch that just landed, marked until something else is
                    //  expanded. Without it a new row joins a list sorted by date
                    //  and the operator has to work out which one is theirs.
                    const isNew = highlightJobId != null && j.id === highlightJobId;
                    return [
                      <tr key={j.id}
                        className={`${isOpen ? 'is-expanded' : ''}${isNew ? ' is-new' : ''}`.trim()}>
                        <td className="ih-x-col">
                          <button
                            type="button"
                            className={`ih-x-btn ${isOpen ? 'is-open' : ''}`}
                            onClick={() => toggleExpand(j.id)}
                            aria-expanded={isOpen}
                            aria-label={isOpen ? 'Hide claims in this batch' : 'Show claims in this batch'}
                            title={isOpen ? 'Hide the claims' : 'Show the claims in this batch'}
                          >
                            <IconChevron size={14} />
                          </button>
                        </td>
                        <td>
                          <span className="ih-fac">{j.facility_name || '—'}</span>
                          {j.short_code && <span className="ih-code">{j.short_code}</span>}
                        </td>
                        {/* The month comes out of the XML's own dateOfService
                            values, not from anything the operator typed. */}
                        <td><span className="ih-month">{j.month_year || '—'}</span></td>
                        <td className="ih-file" title={j.filename || ''}>{j.filename || '—'}</td>
                        <td className="num">{j.inserted}{j.skipped ? <span className="ih-skip"> +{j.skipped} skipped</span> : null}</td>
                        <td className="num">
                          {/* Differs from Claims when rows were voided afterwards. */}
                          <span className={Number(j.claims_present) !== Number(j.inserted) ? 'ih-drift' : ''}>
                            {j.claims_present}
                          </span>
                        </td>
                        <td>
                          <div>{fmtDate(j.started_at)}</div>
                          <div className="ih-time">{fmtTime(j.started_at)}{j.requested_by_name ? ` · ${j.requested_by_name}` : ''}</div>
                        </td>
                        <td>
                          <span className={`ih-status-pill is-${j.status}`}>
                            {j.status === 'done' ? <IconCheck size={12} />
                              : j.status === 'failed' ? <IconClose size={12} /> : null}
                            {j.status}
                          </span>
                          {j.status === 'failed' && j.error && (
                            <div className="ih-error" title={j.error}>{j.error}</div>
                          )}
                        </td>
                        <td className="ih-act-col">
                          <button
                            type="button"
                            className="ih-del-btn"
                            onClick={() => remove(j)}
                            disabled={deletingId === j.id}
                            aria-label={`Delete the ${j.month_year || ''} batch for ${j.facility_name || 'this facility'}`}
                            title="Delete this batch and its claims"
                          >
                            {deletingId === j.id
                              ? <span className="ih-del-spin" aria-hidden="true" />
                              : <IconTrash size={14} />}
                          </button>
                        </td>
                      </tr>,

                      isOpen && (
                        <tr key={`${j.id}-detail`} className="ih-detail-row">
                          <td colSpan={9} className="ih-detail-cell">
                            <BatchDetail
                              detail={d}
                              onPage={(offset) => fetchDetail(j.id, offset)}
                              onRetry={() => fetchDetail(j.id, 0)}
                            />
                          </td>
                        </tr>
                      ),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="ih-foot">
            <button type="button" className="ih-refresh" onClick={load} disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            {facilityId && <span className="ih-filter-note">Showing this facility only</span>}
            {jobs.length > 0 && (
              <span className="ih-filter-note">
                Use the arrow to open a batch and see its claims
              </span>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ============================================================================
//  BatchDetail — what is inside one imported batch
// ============================================================================
//
//  A summary strip over a page of claims. The strip covers the WHOLE batch, not
//  the visible page, which is why it comes from the server rather than being
//  totalled from `claims` here — adding up 25 rows and calling it the batch total
//  is the kind of number that gets copied into a report.
// ── Why claims did not make it in ───────────────────────────────────────────
//
//  `report_json.skipped` is [{ ref, reason, detail }] — written by the importer
//  for every claim it did not insert. Three reasons exist, and they are not the
//  same kind of news:
//
//    already imported   correct, and the only one that SHOULD skip. The same
//                       claim inserted twice is double-billing.
//    no date            the file carried no date of service AND the batch has no
//                       month to fall back on. Rare, and the claim is recoverable
//                       by hand.
//    could not be saved a row-level failure. This is the one worth chasing, and
//                       the one that used to be indistinguishable from the others.
//
//  Grouped by reason rather than listed flat: 263 duplicates is one fact to
//  accept, 263 failures is an investigation, and a flat list of 263 refs hides
//  which of the two you are looking at.
const SKIP_TONE = {
  'already imported': { cls: 'is-info', lead: 'Correctly skipped — importing them again would duplicate them.' },
  'no date': { cls: 'is-warn', lead: 'No date of service in the file and no month on the batch to fall back on.' },
  'could not be saved': { cls: 'is-error', lead: 'These failed while being written. Worth investigating.' },
};

function SkipReport({ report, skipped }) {
  const list = Array.isArray(report?.skipped) ? report.skipped : [];
  const recovered = Array.isArray(report?.recovered) ? report.recovered : [];

  //  Nothing skipped and nothing recovered — say nothing rather than render an
  //  empty green box on every batch.
  if (!skipped && !recovered.length) return null;

  //  A batch imported BEFORE the importer started recording reasons has a count
  //  but no list. Saying so is better than showing the count again with no
  //  explanation, which is what the row already does.
  if (skipped > 0 && list.length === 0) {
    return (
      <div className="ih-d-skips">
        <div className="ih-d-skip is-warn">
          <strong>{fmtNum(skipped)}</strong> claim(s) were skipped, but this batch
          ran before the importer began recording why. Re-importing the file will
          report the reason for each one.
        </div>
      </div>
    );
  }

  const groups = list.reduce((acc, s) => {
    const key = s.reason || 'skipped';
    (acc[key] = acc[key] || []).push(s);
    return acc;
  }, {});

  return (
    <div className="ih-d-skips">
      {Object.entries(groups).map(([reason, rows]) => {
        const tone = SKIP_TONE[reason] || { cls: 'is-warn', lead: null };
        return (
          <div key={reason} className={`ih-d-skip ${tone.cls}`}>
            <div className="ih-d-skip-head">
              <strong>{fmtNum(rows.length)}</strong> claim(s) skipped — {reason}
            </div>
            {tone.lead && <div className="ih-d-skip-lead">{tone.lead}</div>}
            {/*  The refs, so a claim can be found in the original file. Capped:
                 263 refs on screen is not a report, it is a wall. */}
            <div className="ih-d-skip-refs">
              {rows.slice(0, 20).map((r) => r.ref).filter(Boolean).join(', ')}
              {rows.length > 20 && ` … and ${fmtNum(rows.length - 20)} more`}
            </div>
            {/*  One example of the detail per group. For 'could not be saved' that
                 is the database's own message, which is the difference between a
                 bad tariff and a dropped connection. */}
            {rows[0]?.detail && reason === 'could not be saved' && (
              <div className="ih-d-skip-detail">First failure: {rows[0].detail}</div>
            )}
          </div>
        );
      })}

      {/*  Claims that would once have been dropped and were saved instead. A
           recovered value has to be visible or it is a silent guess. */}
      {recovered.length > 0 && (
        <div className="ih-d-skip is-ok">
          <div className="ih-d-skip-head">
            <strong>{fmtNum(recovered.length)}</strong> claim(s) kept with a recovered value
          </div>
          <div className="ih-d-skip-lead">
            These had no date of service in the file. Rather than dropping them, the
            date was taken from elsewhere on the same claim — the source is recorded
            per claim so it can be checked.
          </div>
          <div className="ih-d-skip-refs">
            {recovered.slice(0, 12)
              .map((r) => `${r.ref} (${r.field} from ${r.from})`).join(', ')}
            {recovered.length > 12 && ` … and ${fmtNum(recovered.length - 12)} more`}
          </div>
        </div>
      )}
    </div>
  );
}

function BatchDetail({ detail, onPage, onRetry }) {
  if (!detail || (detail.loading && !detail.claims)) {
    return <div className="ih-d-status">Loading the claims in this batch…</div>;
  }
  if (detail.error) {
    return (
      <div className="ih-d-status is-error">
        Could not load this batch: {detail.error}
        <button type="button" className="ih-d-retry" onClick={onRetry}>Try again</button>
      </div>
    );
  }

  const s = detail.summary || {};
  const claims = detail.claims || [];
  const { limit = PAGE, offset = 0 } = detail.page || {};
  const total = Number(s.total || 0);
  const from = total === 0 ? 0 : offset + 1;
  const to = offset + claims.length;

  if (total === 0) {
    return (
      <div className="ih-d-status">
        This batch has no claims left in the database — they were deleted or voided
        after it ran. The record above is kept so the import itself is still traceable.
      </div>
    );
  }

  return (
    <div className="ih-detail">
      {/* ── Summary strip ─────────────────────────────────────────────────── */}
      <div className="ih-d-stats">
        <Stat label="Claims" value={fmtNum(s.total)} />
        <Stat label="Total value" value={fmtMoney(s.amount)} />
        <Stat
          label="Priced"
          value={`${fmtNum(s.priced)} of ${fmtNum(s.total)}`}
          // Unpriced claims are the ones whose tariff lookup found nothing, so
          // they are worth GHS 0 in the total above. Flagged rather than buried.
          note={s.unpriced ? `${fmtNum(s.unpriced)} unpriced` : null}
          warn={!!s.unpriced}
        />
        <Stat label="Outpatient" value={fmtNum(s.outpatient)} />
        <Stat label="Inpatient" value={fmtNum(s.inpatient)} />
        <Stat
          label="Service dates"
          value={s.first_date === s.last_date
            ? fmtDate(s.first_date)
            : `${fmtDate(s.first_date)} – ${fmtDate(s.last_date)}`}
        />
      </div>

      {Array.isArray(s.byStatus) && s.byStatus.length > 0 && (
        <div className="ih-d-statuses">
          {s.byStatus.map((r) => (
            <span key={r.status} className={`ih-d-chip is-${r.status}`}>
              {fmtNum(r.n)} {r.status}
            </span>
          ))}
        </div>
      )}

      {/* ── WHY CLAIMS WERE SKIPPED ────────────────────────────────────────
          The row above says "+263 skipped" in orange and stopped there. The
          importer has recorded a reason for every one of them since it was
          reworked — `report_json.skipped` is a list of { ref, reason, detail } —
          and the live import panel shows it, but a batch imported yesterday had
          nowhere to show it. So a number nobody could act on was the whole
          account of 263 missing claims.

          Grouped by reason, because that is the unit of action: 263 duplicates is
          one fact to accept, whereas 263 rows that failed to save is an
          investigation. The refs are listed so a claim can actually be found in
          the file. */}
      <SkipReport report={detail.job?.report_json} skipped={Number(detail.job?.skipped || 0)} />

      {/* ── The claims ────────────────────────────────────────────────────── */}
      <div className="ih-d-table-wrap">
        <table className="ih-d-table">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Member no.</th>
              <th>Service date</th>
              <th>Type</th>
              <th>Principal diagnosis</th>
              <th className="num">Lines</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {claims.map((c) => {
              const lines = Number(c.n_diagnoses || 0) + Number(c.n_procedures || 0)
                          + Number(c.n_investigations || 0) + Number(c.n_medications || 0);
              return (
                <tr key={c.id}>
                  <td>
                    <span className="ih-d-name">{c.patient_name || '—'}</span>
                    <span className="ih-d-meta">
                      {c.patient_id}{c.gender ? ` · ${c.gender}` : ''}
                    </span>
                  </td>
                  <td className="ih-d-mono">{c.member_no || '—'}</td>
                  <td className="ih-d-mono">
                    {c.admission_date || '—'}
                    {c.discharge_date && c.discharge_date !== c.admission_date && (
                      <span className="ih-d-meta">to {c.discharge_date}</span>
                    )}
                  </td>
                  <td>
                    <span className={`ih-d-att is-${c.attendance_type}`}>
                      {c.attendance_type === 'inpatient' ? 'IPD'
                        : c.attendance_type === 'outpatient' ? 'OPD'
                        : c.attendance_type}
                    </span>
                  </td>
                  <td className="ih-d-dx" title={c.principal_dx_name || ''}>
                    {c.principal_dx_name || <span className="ih-d-none">not coded</span>}
                    {c.specialty_gdrg_name && (
                      <span className="ih-d-meta">{c.specialty_gdrg_name}</span>
                    )}
                  </td>
                  <td className="num">{lines || <span className="ih-d-none">0</span>}</td>
                  <td className="num">
                    <span className={Number(c.total_amount) > 0 ? '' : 'ih-d-unpriced'}>
                      {fmtMoney(c.total_amount)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ── Paging ────────────────────────────────────────────────────────── */}
      <div className="ih-d-page">
        <span className="ih-d-count">
          Showing {fmtNum(from)}–{fmtNum(to)} of {fmtNum(total)}
        </span>
        <div className="ih-d-page-btns">
          <button
            type="button"
            onClick={() => onPage(Math.max(offset - limit, 0))}
            disabled={offset === 0 || detail.loading}
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => onPage(offset + limit)}
            disabled={to >= total || detail.loading}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, note, warn }) {
  return (
    <div className="ih-d-stat">
      <div className="ih-d-stat-label">{label}</div>
      <div className="ih-d-stat-value">{value}</div>
      {note && <div className={`ih-d-stat-note ${warn ? 'is-warn' : ''}`}>{note}</div>}
    </div>
  );
}
